import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getVisuelPath, resolveMediaUrl } from '../utils/media';
import { resolveMinisiteFont, resolveMinisiteColor, minisiteFontsHref } from '../utils/minisiteTheme';
import { getOccasion, resolveBlocs } from '../utils/minisiteOccasions';
import MinisiteRsvpForm from './MinisiteRsvpForm';
import './Minisite.css';

// Rendu du mini-site de mariage à partir de cartes_v2.params. Un seul composant pour la page
// publique (MinisitePage.js), l'aperçu de l'éditeur (MinisiteEditor.js) et la démo live de la
// fiche produit (ProductDetail.js) : la démo montre exactement ce que le client obtient.
//
// Le fond est le visuel BRUT (getVisuelPath), pas la démo carte-à-gratter (getDemoPath) : cette
// dernière est un mockup de téléphone avec cœur à gratter incrusté dans l'image.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const formatDate = (date) => {
  if (!date || !ISO_DATE.test(date)) return '';
  const formatted = new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const formatHeure = (heure) => (/^\d{2}:\d{2}$/.test(heure || '') ? heure.replace(':', 'h').replace(/h00$/, 'h') : '');

const getRemaining = (date, heure) => {
  if (!date || !ISO_DATE.test(date)) return null;
  const time = /^\d{2}:\d{2}$/.test(heure || '') ? heure : '00:00';
  const ms = new Date(`${date}T${time}:00`).getTime() - Date.now();
  if (ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  return {
    jours: Math.floor(totalSeconds / 86400),
    heures: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    secondes: totalSeconds % 60,
  };
};

const useCountdown = (date, heure) => {
  const [remaining, setRemaining] = useState(() => getRemaining(date, heure));
  useEffect(() => {
    setRemaining(getRemaining(date, heure));
    const timer = setInterval(() => setRemaining(getRemaining(date, heure)), 1000);
    return () => clearInterval(timer);
  }, [date, heure]);
  return remaining;
};

// "Camille & Julien" / "Camille et Julien" → chaque prénom arrive l'un après l'autre.
const splitPrenoms = (prenoms) => {
  const parts = prenoms.split(/\s+(?:&|et)\s+/i);
  return parts.length === 2 ? [parts[0], '&', parts[1]] : [prenoms];
};

// `maps` : coordonnées GPS ("43.52, 5.44") ou lien Google Maps collé par le client. Seuls les
// liens https de Google sont repris tels quels ; sinon recherche sur le lieu + adresse.
const resolveMaps = ({ maps, lieu, adresse }) => {
  const raw = (maps || '').trim();
  const coords = raw.match(/^(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
  const query = coords ? `${coords[1]},${coords[2]}` : [lieu, adresse].filter(Boolean).join(', ');
  if (!query && !raw) return null;
  const isGoogleLink = /^https:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps|(www\.)?google\.[a-z.]+\/maps)/i.test(raw);
  return {
    link: isGoogleLink ? raw : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
    embed: query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : null,
  };
};

const Names = ({ prenoms, animation }) => {
  const parts = splitPrenoms(prenoms);
  if (animation === 'lettres') {
    let charIndex = 0;
    return parts.map((part, partIndex) => (
      <span key={partIndex} className={`ms-name-line${part === '&' ? ' ms-name-amp' : ''}`}>
        {[...part].map((char, i) => {
          charIndex += 1;
          return (
            <span key={i} className="ms-letter" style={{ animationDelay: `${0.5 + charIndex * 0.07}s` }}>
              {char === ' ' ? ' ' : char}
            </span>
          );
        })}
      </span>
    ));
  }
  return parts.map((part, index) => (
    <span
      key={index}
      className={`ms-name-part${part === '&' ? ' ms-name-amp' : ''}`}
      style={{ animationDelay: `${0.6 + index * 0.45}s` }}
    >
      {part}
    </span>
  ));
};

// `statique` : sans animation d'arrivée (captures d'écran des fiches, minisite-factory/).
const MinisiteView = ({ params = {}, slug, embedded = false, statique = false }) => {
  const occasion = getOccasion(params);
  const {
    theme_visuel: visuel, police, police_texte: policeTexte, couleur, ambiance, animation = 'douce',
    disposition = 'centre', taille_prenoms: taillePrenoms = 100, voile = 70,
    intro, prenoms, sous_titre: sousTitre, date, heure, lieu, adresse, texte, signature,
    programme, programme_titre: programmeTitre, compte_a_rebours: compteARebours, carte_lieu: carteLieu,
    details, details_titre: detailsTitre,
    cagnotte_url: cagnotteUrl, cagnotte_titre: cagnotteTitre, cagnotte_texte: cagnotteTexte,
    rsvp_actif: rsvpActif, rsvp_titre: rsvpTitre, rsvp_message: rsvpMessage, rsvp_date_limite: rsvpDateLimite,
    photo, photo_forme: photoForme = 'rond', photo_emplacement: photoEmplacement = 'accueil',
    fond_perso: fondPerso, fond_perso_actif: fondPersoActif, fond_cadrage: fondCadrage = 'centre',
  } = params;
  const remaining = useCountdown(date, heure);
  // Fond : celui envoyé par le client s'il existe (et n'a pas été désactivé), sinon le visuel acheté.
  const useFondPerso = fondPerso && fondPersoActif !== false;
  const bgImage = useFondPerso ? resolveMediaUrl(fondPerso) : (visuel ? resolveMediaUrl(getVisuelPath(visuel)) : null);
  const bgPosition = { haut: 'center top', bas: 'center bottom' }[useFondPerso ? fondCadrage : ''] || 'center';
  const displayedNames = prenoms || occasion.nomsPlaceholder;
  const nameUnits = animation === 'lettres' ? [...displayedNames].length * 0.07 : splitPrenoms(displayedNames).length * 0.45;
  const afterNames = 0.6 + nameUnits;
  const maps = resolveMaps(params);
  const etapes = Array.isArray(programme) ? programme.filter((e) => e && e.titre) : [];
  const lignes = Array.isArray(details) ? details.filter((d) => d && d.valeur) : [];
  const fontsHref = minisiteFontsHref([police, policeTexte]);
  const dateLine = [formatDate(date), formatHeure(heure) && `à ${formatHeure(heure)}`].filter(Boolean).join(' ');
  const photoUrl = photo ? resolveMediaUrl(photo) : null;
  const photoEl = photoUrl && <img className={`ms-photo ms-photo-${photoForme}`} src={photoUrl} alt="" />;
  const cagnotteOk = /^https:\/\//.test(cagnotteUrl || '');

  const title = (text) => <div className="ms-panel-title" role="heading" aria-level="2">{text}</div>;

  // Une section ne s'affiche que si elle a du contenu ou si son interrupteur est activé.
  const blocs = {
    texte: texte && (
      <section className="ms-panel" key="texte">
        <p className="ms-texte">{texte}</p>
        {signature && <p className="ms-signature">{signature}</p>}
      </section>
    ),
    photo: photoEl && photoEmplacement === 'page' && (
      <section className="ms-panel ms-panel-photo" key="photo">{photoEl}</section>
    ),
    details: lignes.length > 0 && (
      <section className="ms-panel" key="details">
        {detailsTitre && title(detailsTitre)}
        <dl className="ms-details">
          {lignes.map((ligne, index) => (
            <div key={index}>
              {ligne.label && <dt>{ligne.label}</dt>}
              <dd>{ligne.valeur}</dd>
            </div>
          ))}
        </dl>
      </section>
    ),
    programme: etapes.length > 0 && (
      <section className="ms-panel" key="programme">
        {title(programmeTitre || 'Le programme')}
        <ul className="ms-programme">
          {etapes.map((etape, index) => (
            <li key={index}>
              <span className="ms-programme-heure">{etape.heure}</span>
              <span className="ms-programme-titre">{etape.titre}</span>
            </li>
          ))}
        </ul>
      </section>
    ),
    compte_a_rebours: remaining && compteARebours !== false && occasion.compteARebours && (
      <section className="ms-panel" key="compte_a_rebours">
        {title(occasion.compteARebours)}
        <div className="ms-countdown">
          {Object.entries(remaining).map(([label, value]) => (
            <div key={label}>
              <span className="ms-countdown-value">{String(value).padStart(2, '0')}</span>
              <span className="ms-countdown-label">{label}</span>
            </div>
          ))}
        </div>
      </section>
    ),
    lieu: (adresse || (lieu && maps)) && (
      <section className="ms-panel" key="lieu">
        {title('Le lieu')}
        {lieu && <p className="ms-texte"><strong>{lieu}</strong></p>}
        {adresse && <p className="ms-texte">{adresse}</p>}
        {maps?.embed && carteLieu !== false && (
          <iframe className="ms-map" title="Plan d'accès" src={maps.embed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        )}
        {maps && <a className="ms-link" href={maps.link} target="_blank" rel="noreferrer">Ouvrir dans Google Maps</a>}
      </section>
    ),
    rsvp: rsvpActif && (
      <section className="ms-panel" key="rsvp">
        {title(rsvpTitre || 'Confirmez votre présence')}
        {rsvpMessage && <p className="ms-texte">{rsvpMessage}</p>}
        {formatDate(rsvpDateLimite) && (
          <p className="ms-muted">Réponse souhaitée avant le {formatDate(rsvpDateLimite).toLowerCase()}</p>
        )}
        <MinisiteRsvpForm slug={slug} params={params} preview={embedded} />
      </section>
    ),
    cagnotte: cagnotteOk && (
      <section className="ms-panel" key="cagnotte">
        {title(cagnotteTitre || 'Un petit geste')}
        {cagnotteTexte && <p className="ms-texte">{cagnotteTexte}</p>}
        <a className="ms-link" href={cagnotteUrl} target="_blank" rel="noreferrer noopener">Participer</a>
      </section>
    ),
  };

  return (
    <div
      className={`ms-root ${embedded ? 'ms-embedded' : 'ms-fullpage'} ms-ambiance-${ambiance === 'sombre' ? 'sombre' : 'claire'} ms-anim-${animation}${statique ? ' ms-static' : ''}`}
      style={{
        '--ms-font': resolveMinisiteFont(police),
        '--ms-font-texte': resolveMinisiteFont(policeTexte, 'dm-sans'),
        '--ms-accent': resolveMinisiteColor(couleur),
        '--ms-names-scale': Math.max(60, Math.min(160, Number(taillePrenoms) || 100)) / 100,
        '--ms-voile': Math.max(0, Math.min(100, Number(voile))) / 100,
      }}
    >
      {fontsHref && <Helmet><link rel="stylesheet" href={fontsHref} /></Helmet>}
      <div className="ms-bg" style={bgImage ? { backgroundImage: `url("${bgImage}")`, backgroundPosition: bgPosition } : undefined} />
      <div className="ms-scroll">
        <section className={`ms-hero ms-hero-${disposition}`}>
          <div className="ms-hero-inner">
            {photoEl && photoEmplacement !== 'page' && <div className="ms-anim" style={{ animationDelay: '0.1s' }}>{photoEl}</div>}
            <p className="ms-intro ms-anim" style={{ animationDelay: '0.2s' }}>{intro || occasion.intro}</p>
            {/* div et non h1/h2 : App.css impose police/taille/couleur des titres en !important. */}
            <div className="ms-names" role="heading" aria-level="1">
              <Names prenoms={displayedNames} animation={animation} />
            </div>
            {sousTitre && (
              <p className="ms-sous-titre ms-anim" style={{ animationDelay: `${afterNames + 0.2}s` }}>{sousTitre}</p>
            )}
            <div className="ms-divider ms-anim" style={{ animationDelay: `${afterNames + 0.4}s` }} />
            <p className="ms-date ms-anim" style={{ animationDelay: `${afterNames + 0.7}s` }}>
              {dateLine || occasion.dateLabel}
            </p>
            {(lieu || !prenoms) && (
              <p className="ms-lieu ms-anim" style={{ animationDelay: `${afterNames + 1}s` }}>{lieu || 'Lieu'}</p>
            )}
          </div>
          <div className="ms-scroll-hint ms-anim" style={{ animationDelay: `${afterNames + 1.7}s` }}>
            <span>⌄</span>
          </div>
        </section>

        {resolveBlocs(params).map((key) => blocs[key] || null)}

        <footer className="ms-footer">Créé avec Joli Ptit Mot</footer>
      </div>
    </div>
  );
};

export default MinisiteView;
